import { appOrigin } from '../navigation';
import { determinationToolBuildUrl } from '../../determinations-shared/services/determination-tool-url-patterns';
import { slugFromName } from '../../tri-shared/services/formatting';
import type { FacilityBrief, ISO8601DateOnly } from '../../cross-app-shared/types/common';
import { platformAppArtwork } from './platform-artwork';
import type { AppID, FacilityID, TenantID } from '../../cross-app-shared/types/db.ts';
import {
  type PlatformLink,
  type PlatformPages,
  platformReportingYear,
  type PlatformStatus,
  type PlatformToolAccess,
  type PlatformToolStatusAggregated,
  type PlatformToolStatusItem,
} from '../../cross-app-shared/types/platform-landing-types.ts';

// The order tools appear in on the orbit, clockwise from the top after the centre tool, which also drives their pop-in
// animation order. The orbit lays itself out from this list, so adding or removing a tool here is all it takes, as long
// as its artwork is in platform-orbit.svg. Artwork for tools not listed here (e.g. Consulting) is hidden.
export const platformToolKeys = [
  'compliance',
  'hazwaste',
  'tierii',
  'tri',
  'parsing',
  'emissions',
  'determinations',
] as const;

// A tool's overall state: the most urgent of its statuses, pending while they load (or if they failed to), or locked
// when the facilities shown have no access to it.
export type PlatformToolState = PlatformStatus | 'pending' | 'locked';

// Access is known before statuses are: it comes from the facility list every app has already loaded, so tools can be
// linked straight away. `items` is null until the backend's statuses arrive.
export type PlatformToolAccessShown = PlatformToolAccess | { granted: true; items: null };

export interface PlatformTool {
  name: string;
  // Prefix of the `<prefix>_icon` and `<prefix>_label` element IDs in platform-orbit.svg.
  svgPrefix: string;
  // The tool's icon on its own, sliced out of platform-orbit.svg.
  iconUrl: string;
  // The app this tool links to, or null when it isn't an app.
  app: AppID | null;
  access: PlatformToolAccessShown;
}

export type PlatformTools = Record<AppID, PlatformTool>;

// Until the statuses have been fetched, a tool's access comes from the apps annotated on the facilities being shown. The
// backend resolves both the same way (see appAccessResolve), so the two agree.
export function platformToolsBuild(
  statuses: PlatformToolStatusAggregated | null,
  facilities: FacilityBrief[],
): PlatformTools {
  const appsGranted = new Set(facilities.flatMap((f) => f.apps));
  const access = (key: AppID): PlatformToolAccessShown => {
    if (statuses) return statuses[key];
    return appsGranted.has(key) ? { granted: true, items: null } : { granted: false };
  };
  return Object.fromEntries<PlatformTool>(
    platformToolKeys.map((key) => [key, { ...platformAppArtwork[key], access: access(key) }]),
  ) as PlatformTools;
}

// What the page shows: one facility, or every facility the user can see in a tenant. The page's URL carries a facility
// and whether to show all of its tenant's facilities, so that toggling back to the facility returns to it.
export type PlatformScopeParams = { tenantID: TenantID | null; facilityID: FacilityID | null; all: boolean };

export type PlatformScope = {
  tenant: { tenantID: TenantID; name: string };
  // Every facility the user can see in the tenant.
  tenantFacilities: FacilityBrief[];
} & (
  | { kind: 'facility'; facility: FacilityBrief }
  // `facility` is the one to return to when toggling off "All facilities", if there is one.
  | { kind: 'tenant'; facility: FacilityBrief | null }
);

// Resolves the page's URL against the facilities the user can see. A facility the user can't see falls back to its
// tenant, then the user's primary tenant, then their first. A tenant with a single facility is shown as that facility,
// as the two are the same. Null when the user can't see any facilities.
export function platformScopeResolve(
  params: PlatformScopeParams,
  facilities: FacilityBrief[],
  primaryTenantID: TenantID | null,
): PlatformScope | null {
  const facilitiesInTenant = (tenantID: TenantID | null) =>
    tenantID === null ? [] : facilities.filter((f) => f.tenantID === tenantID);

  const facility = facilities.find((f) => f.facilityID === params.facilityID) ?? null;
  let tenantFacilities = facilitiesInTenant(facility?.tenantID ?? params.tenantID);
  if (tenantFacilities.length === 0) tenantFacilities = facilitiesInTenant(primaryTenantID);
  if (tenantFacilities.length === 0) tenantFacilities = facilitiesInTenant(facilities[0]?.tenantID ?? null);
  if (tenantFacilities.length === 0) return null;

  const first = tenantFacilities[0];
  const tenant = { tenantID: first.tenantID, name: first.tenantName };
  if (tenantFacilities.length === 1) return { kind: 'facility', tenant, tenantFacilities, facility: first };
  if (facility && !params.all) return { kind: 'facility', tenant, tenantFacilities, facility };
  return { kind: 'tenant', tenant, tenantFacilities, facility };
}

// The facilities whose statuses are shown.
export function platformScopeFacilities(scope: PlatformScope): FacilityBrief[] {
  return scope.kind === 'facility' ? [scope.facility] : scope.tenantFacilities;
}

export function platformScopeParams(scope: PlatformScope): PlatformScopeParams {
  return {
    tenantID: scope.tenant.tenantID,
    facilityID: scope.facility?.facilityID ?? null,
    all: scope.kind === 'tenant',
  };
}

// Placeholder statuses from the design mockup, for tests and for previewing the page with data.
export const platformToolsExample: PlatformTools = platformToolsBuild(
  {
    compliance: {
      granted: true,
      items: [
        { status: 'needs', text: '2 tasks need you today', count: 2, due: null, link: null },
        { status: 'soon', text: '5 due this month', due: null, link: null },
      ],
    },
    hazwaste: { granted: false },
    tierii: {
      granted: true,
      items: [{ status: 'soon', text: '3 facilities to update', due: '2027-03-01' as ISO8601DateOnly, link: null }],
    },
    tri: { granted: true, items: [{ status: 'clear', text: '2025 Form R submitted', link: null }] },
    parsing: {
      granted: true,
      items: [{ status: 'soon', text: '2 new permits ready to review', due: null, link: null }],
    },
    emissions: {
      granted: true,
      items: [{ status: 'soon', text: 'Q3 emissions report', due: '2026-10-30' as ISO8601DateOnly, link: null }],
    },
    determinations: {
      granted: true,
      items: [{ status: 'clear', text: 'Determinations completed for 2026', link: null }],
    },
  },
  [],
);

export const platformStatusColors: Record<PlatformStatus, string> = {
  needs: '#FF5D1D',
  soon: '#FFC400',
  clear: '#0FA96A',
};

export const platformToolStateColors: Record<PlatformToolState, string> = {
  ...platformStatusColors,
  pending: '#92A5B9',
  locked: '#5F6A94',
};

export function platformToolItems(tool: PlatformTool): PlatformToolStatusItem[] {
  return tool.access.granted ? (tool.access.items ?? []) : [];
}

// Where the tool's tile links to: its home page. Null when it isn't an app or the tenant has no access.
export function platformToolHref(tool: PlatformTool, scope: PlatformScope | null): string | null {
  return tool.access.granted && tool.app && scope ? platformLinkHref(platformToolHomeLink(tool.app), scope) : null;
}

// Where to go to deal with an item, or null when there's nothing more specific to do than open its tool.
export function platformItemHref(item: PlatformToolStatusItem, scope: PlatformScope | null): string | null {
  return item.link && scope ? platformLinkHref(item.link, scope) : null;
}

function platformToolHomeLink(app: AppID): PlatformLink {
  switch (app) {
    case 'tri':
      return { app, page: 'status', facilityID: null };
    case 'emissions':
    case 'tierii':
      return { app, page: 'overview', facilityID: null };
    case 'hazwaste':
    case 'determinations':
      return { app, page: 'home', facilityID: null };
    case 'compliance':
      return { app, page: 'facilityTasks', facilityID: null };
    case 'parsing':
      return { app, page: 'permitIndexing', facilityID: null };
  }
}

const platformTriPaths: Record<PlatformPages['tri'], string> = {
  status: '',
  products: '/products',
  usage: '/usage',
  thresholds: '/thresholds',
  releases: '/releases',
  formR: '/form-r',
  formRSubmit: '/submit',
};

const platformEmissionsPaths: Record<PlatformPages['emissions'], string> = {
  overview: '/emissions-overview',
  units: '/emissions-units',
};

// The overview isn't tied to a year, unlike the rest of Tier II.
const platformTierIIPaths: Record<Exclude<PlatformPages['tierii'], 'overview'>, string> = {
  products: '/products',
  inventoryQuestionnaire: '/inventory/questionnaire',
  inventoryThreshold: '/inventory/threshold',
  reportForm: '/report-form',
  export: '/export',
};

const platformDeterminationsPages: Record<PlatformPages['determinations'], 'home' | 'portal' | 'compliance'> = {
  home: 'home',
  portal: 'portal',
  complianceIssues: 'compliance',
};

// A link's URL, opened on what the page is showing so the user doesn't have to find it again (see PlatformLink).
// Apps that can show several facilities at once are opened on all of a tenant's; the rest open on the facility the page
// would return to, or the tenant's first.
export function platformLinkHref(link: PlatformLink, scope: PlatformScope): string {
  const facility =
    scope.tenantFacilities.find((f) => f.facilityID === link.facilityID) ?? scope.facility ?? scope.tenantFacilities[0];
  const facilityPath = `facility/${facility.facilityID}-${slugFromName(facility.name)}`;
  const reportingYear = platformReportingYear(new Date());
  const facilitiesQuery = `facilities=${link.facilityID === null && scope.kind === 'tenant' ? 'all' : facility.facilityID}`;
  const origin = appOrigin(link.app);

  switch (link.app) {
    case 'tri':
      return `${origin}/${reportingYear}/${facilityPath}${platformTriPaths[link.page]}`;
    case 'emissions':
      if (link.page === 'questionnaire') {
        // The questionnaire page shows the overview of every unit's questions when given unit 0:
        const unit = link.emissionsUnit
          ? `${link.emissionsUnit.emissionsUnitID}-${slugFromName(link.emissionsUnit.name)}`
          : '0-pick-one';
        return `${origin}/${reportingYear}/${facilityPath}/emissions-units/${unit}/questionnaire`;
      }
      return `${origin}/${reportingYear}/${facilityPath}${platformEmissionsPaths[link.page]}`;
    case 'tierii':
      return link.page === 'overview'
        ? `${origin}/${facilityPath}/overview`
        : `${origin}/${reportingYear}/${facilityPath}${platformTierIIPaths[link.page]}`;
    case 'compliance':
      return `${origin}/${facility.tenantID}/facility/tasks?${facilitiesQuery}`;
    case 'parsing':
      return `${origin}/${facility.tenantID}/facility/permit-indexing?${facilitiesQuery}`;
    case 'determinations':
      return determinationToolBuildUrl(
        { page: platformDeterminationsPages[link.page], tenantID: facility.tenantID, facilityID: facility.facilityID },
        origin,
      );
    case 'hazwaste':
      // TODO: 1. Hazwaste - Link to the facility once the app is live.
      return origin;
  }
}

export function platformToolState(tool: PlatformTool): PlatformToolState {
  if (!tool.access.granted) return 'locked';
  const items = tool.access.items;
  if (items === null) return 'pending';
  if (items.some((item) => item.status === 'needs')) return 'needs';
  if (items.some((item) => item.status === 'soon')) return 'soon';
  return 'clear';
}

export function platformToolNeedsCount(tool: PlatformTool): number {
  return platformToolItems(tool).reduce((total, item) => total + (item.status === 'needs' ? item.count : 0), 0);
}

// "Needs you" items count by how many things need attention; "coming up" items count once each. All-clear items aren't
// counted: how many things are fine depends only on how finely each app splits up its work.
export function platformStatusCounts(tools: PlatformTools): { needs: number; soon: number } {
  const counts = { needs: 0, soon: 0 };
  for (const key of platformToolKeys) {
    for (const item of platformToolItems(tools[key])) {
      if (item.status === 'needs') counts.needs += item.count;
      if (item.status === 'soon') counts.soon += 1;
    }
  }
  return counts;
}

const platformToolStateOrder: PlatformToolState[] = ['needs', 'soon', 'clear', 'pending', 'locked'];

// Most urgent first, locked tools last, then in orbit order. While statuses load every accessible tool is pending, so the
// list starts in orbit order and reorders once they arrive.
export function platformToolsByState(
  tools: PlatformTools,
): { key: AppID; tool: PlatformTool; state: PlatformToolState }[] {
  return platformToolKeys
    .map((key) => ({ key, tool: tools[key], state: platformToolState(tools[key]) }))
    .sort((a, b) => platformToolStateOrder.indexOf(a.state) - platformToolStateOrder.indexOf(b.state));
}

export type PlatformView = 'orbit' | 'list';

// The orbit layouts are drawn in design units and scaled to fit. The wide layout is the full design; the narrow
// layout drops the welcome copy and the planet, leaving just the orbit and the "today" panel.
export const PLATFORM_WIDE_WIDTH = 1600;
export const PLATFORM_NARROW_WIDTH = 1164;
export const PLATFORM_HEIGHT = 900;
// Below this scale the orbit's text gets too small to read, so we fall back to the list.
const PLATFORM_ORBIT_MIN_SCALE = 0.55;
// The wide layout is preferred as long as it isn't much smaller than the narrow one.
const PLATFORM_WIDE_MIN_RELATIVE_SCALE = 0.85;

export type PlatformLayout =
  | { kind: 'list'; toggleable: boolean }
  | { kind: 'orbit'; wide: boolean; scale: number; stageWidth: number; stageHeight: number };

// Picks a layout for a container of the given size in CSS pixels. Orbit stages always fill the container exactly:
// the stage is at least the design size in both dimensions, and extended in whichever dimension has room to spare.
export function platformLayout(width: number, height: number, preference: PlatformView): PlatformLayout {
  const wideScale = Math.min(width / PLATFORM_WIDE_WIDTH, height / PLATFORM_HEIGHT);
  const narrowScale = Math.min(width / PLATFORM_NARROW_WIDTH, height / PLATFORM_HEIGHT);
  if (narrowScale < PLATFORM_ORBIT_MIN_SCALE) return { kind: 'list', toggleable: false };
  if (preference === 'list') return { kind: 'list', toggleable: true };
  const wide = wideScale >= narrowScale * PLATFORM_WIDE_MIN_RELATIVE_SCALE;
  const scale = wide ? wideScale : narrowScale;
  return { kind: 'orbit', wide, scale, stageWidth: width / scale, stageHeight: height / scale };
}

// Whole days from `today` until `due`; negative when overdue.
export function platformDaysUntil(due: ISO8601DateOnly, today: ISO8601DateOnly): number {
  return Math.round((Date.parse(due) - Date.parse(today)) / 86_400_000);
}

// "Oct 30" this year, "Mar 1, 2027" otherwise.
export function platformDueDateText(due: ISO8601DateOnly, today: ISO8601DateOnly): string {
  return new Date(Date.parse(due)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: due.slice(0, 4) === today.slice(0, 4) ? undefined : 'numeric',
    timeZone: 'UTC',
  });
}

export function platformDaysText(days: number): string {
  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
}

export function platformItemText(item: PlatformToolStatusItem, today: ISO8601DateOnly): string {
  if (!('due' in item) || item.due === null) {
    return item.text;
  } else {
    return `${item.text}${item.text.endsWith('.') ? ' Due ' : ', due '}${platformDueDateText(item.due, today)}.`;
  }
}

export type PlatformNextUp = {
  tool: PlatformTool;
  item: PlatformToolStatusItem;
  due: { date: ISO8601DateOnly; days: number } | null;
};

// The item with the soonest due date that hasn't passed yet, across all tools. Ties go to orbit order. With nothing
// coming due, the first item in orbit order that needs the user, or failing that, that's coming up.
export function platformNextUp(tools: PlatformTools, today: ISO8601DateOnly): PlatformNextUp | null {
  let next: (PlatformNextUp & { due: NonNullable<PlatformNextUp['due']> }) | null = null;
  for (const key of platformToolKeys) {
    for (const item of platformToolItems(tools[key])) {
      if (!('due' in item) || item.due === null) {
        continue;
      }
      const days = platformDaysUntil(item.due, today);
      if (days >= 0 && (next === null || days < next.due.days)) {
        next = { tool: tools[key], item, due: { date: item.due, days } };
      }
    }
  }
  if (next) return next;

  for (const status of ['needs', 'soon'] as const) {
    for (const key of platformToolKeys) {
      const item = platformToolItems(tools[key]).find((item) => item.status === status);
      if (item) return { tool: tools[key], item, due: null };
    }
  }
  return null;
}

// Where to start: the first linked app, in orbit order, with something that needs the user, or failing that, with
// something coming up. Opens on that item's own page when it has one, or else the app's home page.
export function platformStart(
  tools: PlatformTools,
  scope: PlatformScope | null,
): { status: 'needs' | 'soon'; href: string } | null {
  for (const status of ['needs', 'soon'] as const) {
    for (const key of platformToolKeys) {
      const tool = tools[key];
      const toolHref = platformToolHref(tool, scope);
      if (!toolHref || platformToolState(tool) !== status) continue;
      const item = platformToolItems(tool).find((item) => item.status === status && item.link !== null);
      return { status, href: (item && platformItemHref(item, scope)) ?? toolHref };
    }
  }
  return null;
}

// The first word of a user's name, for the greeting. Some users' names are just their email address, which makes for
// an odd greeting, so those are treated as having no name.
export function platformFirstName(name: string): string | null {
  const first = name.trim().split(/\s+/)[0];
  return first && !first.includes('@') ? first : null;
}

// The tenant's name on the flag, fitted to the white part of the cloth in flag units.
export const PLATFORM_FLAG_TEXT_WIDTH = 172;
const PLATFORM_FLAG_TEXT_HEIGHT = 84;
const PLATFORM_FLAG_FONT_SIZES = [34, 30, 26, 22, 18];
const PLATFORM_FLAG_MAX_LINES = 3;

export interface PlatformFlagText {
  // A squeezed line is compressed horizontally to PLATFORM_FLAG_TEXT_WIDTH, as a last resort.
  lines: { text: string; squeeze: boolean }[];
  fontSize: number;
  // Whether the design's letter spacing is kept. It's dropped before resorting to a smaller font.
  tracked: boolean;
}

// Picks the largest font size at which the name fits, preferring fewer lines, then the design's letter spacing. If it
// doesn't fit even at the smallest size, it uses the layout that needs the least squeezing and squeezes only the lines
// that overflow, so the name is always shown in full. Lines are all caps with no descenders, so a line takes up
// roughly its font size in height. `measure` returns the rendered width of a line.
export function platformFlagText(
  name: string,
  measure: (line: string, fontSize: number, tracked: boolean) => number,
): PlatformFlagText {
  const words = name.toUpperCase().split(/\s+/).filter(Boolean);
  const lineCounts = (fontSize: number) =>
    Array.from(
      { length: Math.min(PLATFORM_FLAG_MAX_LINES, words.length, Math.floor(PLATFORM_FLAG_TEXT_HEIGHT / fontSize)) },
      (_, i) => i + 1,
    );

  for (const fontSize of PLATFORM_FLAG_FONT_SIZES) {
    for (const lineCount of lineCounts(fontSize)) {
      for (const tracked of [true, false]) {
        const width = (line: string) => measure(line, fontSize, tracked);
        const lines = platformFlagLinesBalanced(words, lineCount, width);
        if (lines.every((line) => width(line) <= PLATFORM_FLAG_TEXT_WIDTH)) {
          return { lines: lines.map((text) => ({ text, squeeze: false })), fontSize, tracked };
        }
      }
    }
  }

  const fontSize = PLATFORM_FLAG_FONT_SIZES.at(-1)!;
  const width = (line: string) => measure(line, fontSize, false);
  let best: string[] = [];
  for (const lineCount of lineCounts(fontSize)) {
    const lines = platformFlagLinesBalanced(words, lineCount, width);
    if (best.length === 0 || Math.max(...lines.map(width)) < Math.max(...best.map(width))) best = lines;
  }
  return {
    lines: best.map((text) => ({ text, squeeze: width(text) > PLATFORM_FLAG_TEXT_WIDTH })),
    fontSize,
    tracked: false,
  };
}

// The split of words into `lineCount` lines whose widest line is the narrowest.
function platformFlagLinesBalanced(words: string[], lineCount: number, width: (line: string) => number): string[] {
  let best: string[] = [];
  let bestWidth = Infinity;
  for (const lines of platformFlagLineSplits(words, lineCount)) {
    const widest = Math.max(...lines.map(width));
    if (widest < bestWidth) {
      best = lines;
      bestWidth = widest;
    }
  }
  return best;
}

function* platformFlagLineSplits(words: string[], lineCount: number): Generator<string[]> {
  if (lineCount === 1) {
    yield [words.join(' ')];
    return;
  }
  for (let i = 1; i <= words.length - lineCount + 1; i++) {
    for (const rest of platformFlagLineSplits(words.slice(i), lineCount - 1)) {
      yield [words.slice(0, i).join(' '), ...rest];
    }
  }
}

// ---- Orbit layout, in the orbit artwork's coordinates ----

export interface PlatformPoint {
  x: number;
  y: number;
}

export interface PlatformBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PlatformEllipse {
  cx: number;
  cy: number;
  rx: number;
  ry: number;
}

// The tool drawn in the middle of the orbit rather than around it.
export const PLATFORM_CENTRE_TOOL: AppID = 'compliance';
// How far a dot's centre must be from any tool's artwork and from other dots. A dot is 39 across its faint outer ring,
// and a "needs you" badge 58 with its border.
const PLATFORM_DOT_CLEARANCE = 70;

export function platformBoxUnion(boxes: PlatformBox[]): PlatformBox {
  let x1 = Infinity;
  let y1 = Infinity;
  let x2 = -Infinity;
  let y2 = -Infinity;
  for (const b of boxes) {
    if (!b.width) continue;
    x1 = Math.min(x1, b.x);
    y1 = Math.min(y1, b.y);
    x2 = Math.max(x2, b.x + b.width);
    y2 = Math.max(y2, b.y + b.height);
  }
  return { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
}

// Copies fields explicitly, as boxes from getBBox() keep them on their prototype, out of reach of object spread.
export function platformBoxTranslate(box: PlatformBox, dx: number, dy: number): PlatformBox {
  return { x: box.x + dx, y: box.y + dy, width: box.width, height: box.height };
}

function platformBoxCentre(box: PlatformBox): PlatformPoint {
  return { x: box.x + box.width / 2, y: box.y + box.height / 2 };
}

function platformBoxDistance(box: PlatformBox, p: PlatformPoint): number {
  const dx = Math.max(box.x - p.x, 0, p.x - (box.x + box.width));
  const dy = Math.max(box.y - p.y, 0, p.y - (box.y + box.height));
  return Math.hypot(dx, dy);
}

function platformEllipsePoint(ellipse: PlatformEllipse, angle: number): PlatformPoint {
  return { x: ellipse.cx + ellipse.rx * Math.cos(angle), y: ellipse.cy + ellipse.ry * Math.sin(angle) };
}

// The point on an ellipse in the direction of (x, y) from its centre.
export function platformEllipsePointToward(ellipse: PlatformEllipse, x: number, y: number): PlatformPoint {
  return platformEllipsePoint(ellipse, Math.atan2((y - ellipse.cy) / ellipse.ry, (x - ellipse.cx) / ellipse.rx));
}

export interface PlatformOrbitPlacement {
  // How far to move the tool's artwork from where the designer drew it.
  dx: number;
  dy: number;
  // Where to draw the tool's status dot.
  dot: PlatformPoint;
}

// Places the tools that go around the orbit, so tools can be added or removed without redrawing the artwork. The
// designer put every tool in the band between the middle and outer rings, with its dot on one of those rings. So:
// - Tools are spread evenly around the middle of that band, clockwise, each moved as a unit to keep the designer's
//   arrangement of its icon and label. They're offset half a step from the top, so no tool sits dead on the top or
//   bottom, where the band is thinnest.
// - Each dot goes on the middle or outer ring, as close to in line with its tool as it can while staying clear of all
//   the artwork and the other dots, and nearer its own tool than any other.
// `rings` run from inner to outer, and `obstacles` are any other artwork the dots must avoid.
export function platformOrbitPlacements(
  units: { unit: PlatformBox; icon: PlatformBox }[],
  rings: PlatformEllipse[],
  obstacles: PlatformBox[],
): PlatformOrbitPlacement[] {
  const middle = rings[rings.length - 2];
  const outer = rings[rings.length - 1];
  const band = { cx: middle.cx, cy: middle.cy, rx: (middle.rx + outer.rx) / 2, ry: (middle.ry + outer.ry) / 2 };

  const moved = units.map(({ unit }, i) => {
    const angle = platformOrbitAngle(i, units.length);
    const target = platformEllipsePoint(band, angle);
    const centre = platformBoxCentre(unit);
    const dx = target.x - centre.x;
    const dy = target.y - centre.y;
    return { angle, dx, dy, unit: platformBoxTranslate(unit, dx, dy) };
  });

  const blockers = [...obstacles, ...moved.map((m) => m.unit)];
  const dots: PlatformPoint[] = [];
  return moved.map(({ angle, dx, dy, unit }, i) => {
    const others = moved.filter((_, j) => j !== i).map((m) => m.unit);
    let dot: PlatformPoint | null = null;
    let dotScore = Infinity;
    for (const ring of [middle, outer]) {
      for (let degrees = 0; degrees < 360; degrees++) {
        const candidateAngle = (degrees * Math.PI) / 180;
        const p = platformEllipsePoint(ring, candidateAngle);
        if (blockers.some((b) => platformBoxDistance(b, p) < PLATFORM_DOT_CLEARANCE)) continue;
        if (dots.some((d) => Math.hypot(d.x - p.x, d.y - p.y) < 2 * PLATFORM_DOT_CLEARANCE)) continue;
        const own = platformBoxDistance(unit, p);
        if (others.some((b) => platformBoxDistance(b, p) <= own)) continue;
        // Closest in angle to the tool wins; between the two rings at the same angle, the one nearer the tool.
        const angleOff = Math.abs(Math.atan2(Math.sin(candidateAngle - angle), Math.cos(candidateAngle - angle)));
        const score = angleOff + own / 1e6;
        if (score < dotScore) {
          dot = p;
          dotScore = score;
        }
      }
    }
    // Crowded enough that nowhere is clear: fall back to the middle ring, in line with the tool.
    dot ??= platformEllipsePoint(middle, angle);
    dots.push(dot);
    return { dx, dy, dot };
  });
}

// The angle, around the orbit's ellipses, of the i-th of `count` tools: clockwise from just right of the top.
export function platformOrbitAngle(i: number, count: number): number {
  return -Math.PI / 2 + ((i + 0.5) * 2 * Math.PI) / count;
}
